/**
 * The drafts side panel: a 360px region beside the chat (full width under
 * 760px, an overlay under 1100px), header strip with the title and a close
 * control, and the artifact rendered below — a quiz as question cards with the
 * right answer ticked, markdown as text. "Open in Compose" is hidden until the
 * desktop app can do it.
 *
 * Chat.tsx portals this into `useShell().panelHost`, a `display:contents` flex
 * sibling of the sidebar and the main region, so the width and position below
 * are the whole of the layout — there is no wrapper sizing it from outside.
 * The Agents panel shares the frame (`PanelFrame`), and where a chat has both
 * the header holds "Drafts | Agents" (`PanelTabs`) in place of the title.
 */

import type { CSSProperties, ReactNode } from 'react';
import type { Message, QuizQuestion } from '../api';
import { Ph } from './ui';

export type PanelTab = 'drafts' | 'agents';

/** Drafts and Agents, in the header of a chat that has both. */
export function PanelTabs({ value, onChange }: { value: PanelTab; onChange: (tab: PanelTab) => void }) {
  const tab = (id: PanelTab, label: string) => (
    <button
      type="button"
      role="tab"
      className="bare"
      aria-selected={value === id}
      onClick={() => onChange(id)}
      style={{
        height: 30,
        display: 'flex',
        alignItems: 'center',
        padding: '0 10px',
        borderRadius: 'var(--radius-chrome)',
        fontSize: 13,
        ...(value === id ? { background: 'var(--primary-soft)', color: 'var(--primary-text)', fontWeight: 500 } : { color: 'var(--text-muted)' })
      }}
    >
      {label}
    </button>
  );
  return (
    <div role="tablist" style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
      {tab('drafts', 'Drafts')}
      {tab('agents', 'Agents')}
    </div>
  );
}

/** The panel's region and header strip; `head` fills the header up to the
 *  close control. */
export function PanelFrame({
  head,
  onClose,
  overlay,
  narrow,
  width = 360,
  first = false,
  children
}: {
  head: ReactNode;
  onClose: () => void;
  overlay: boolean;
  narrow: boolean;
  /** Wider for what is drawn in it, such as an artifact's page. */
  width?: CSSProperties['width'];
  /** Before the chat rather than after it, sharing the width with it, the
   *  larger part its own: design mode's layout. */
  first?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      style={{
        width: narrow ? '100%' : width,
        flex: 'none',
        ...(first && !overlay && !narrow ? { width: 'auto', flex: '1.6 1 0', minWidth: 0, maxWidth: 1040 } : {}),
        order: first && !overlay ? 0 : 2,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--app-panel)',
        borderRadius: 'var(--radius-region)',
        overflow: 'hidden',
        position: overlay ? 'absolute' : 'relative',
        top: 0,
        right: 0,
        bottom: 0,
        zIndex: 25,
        boxShadow: overlay ? 'var(--shadow-xl)' : 'none'
      }}
    >
      <div
        style={{
          flex: 'none',
          height: 52,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '0 10px 0 16px',
          background: 'var(--app-panel-alt)',
          borderBottom: '1px solid var(--border-hairline)'
        }}
      >
        {head}
        <button
          type="button"
          className="bare hov-soft"
          aria-label="Close"
          onClick={onClose}
          style={{
            width: 30,
            height: 30,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 6,
            color: 'var(--text-muted)'
          }}
        >
          <Ph name="x" size={15} />
        </button>
      </div>
      {children}
    </section>
  );
}

export function PanelTitle({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        flex: 1,
        minWidth: 0,
        fontWeight: 600,
        fontSize: 14,
        color: 'var(--text-strong)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      }}
    >
      {children}
    </span>
  );
}

/** A quiz as question cards, the right answer ticked. */
export function QuizCards({ questions }: { questions: readonly QuizQuestion[] }) {
  return (
    <>
      {questions.map((q, i) => (
        <div
          key={i}
          style={{
            background: 'var(--surface-card)',
            borderRadius: 'var(--radius-app-card)',
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            gap: 8
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>{q.q}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-body)' }}>
            <Ph name="check-circle" fill size={15} color="var(--signal)" />
            {q.a}
          </div>
          {q.wrong.map((w, j) => (
            <div key={j} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-muted)' }}>
              <Ph name="circle" size={15} />
              {w}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

export function ArtifactPanel({
  message,
  tabs,
  onClose,
  overlay,
  narrow
}: {
  message: Message;
  /** Drafts and Agents, where the chat has both: the title moves down. */
  tabs?: ReactNode;
  onClose: () => void;
  overlay: boolean;
  narrow: boolean;
}) {
  if (message.body.kind !== 'artifact') return null;
  const { title, doc } = message.body;

  return (
    <PanelFrame head={tabs ?? <PanelTitle>{title}</PanelTitle>} onClose={onClose} overlay={overlay} narrow={narrow}>
      <div
        className="scroll selectable"
        style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}
      >
        {tabs ? <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-strong)' }}>{title}</span> : null}
        {doc.type === 'quiz' ? (
          <QuizCards questions={doc.questions} />
        ) : (
          <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--text-body)', whiteSpace: 'pre-wrap' }}>{doc.text}</div>
        )}
      </div>
    </PanelFrame>
  );
}
